/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

export type HashRecord<T> = Record<string, T>;
export type HashParams = HashRecord<string>;
export type HashUpdates = HashRecord<string | null>;
export type ScrollToElementFn = (
  elementId: string,
  scrollOptions?: ScrollIntoViewOptions
) => void;

const HASH_KEY_PATTERN = /^[a-zA-Z0-9_-]+$/;
const isSafeHashKey = (key: string): boolean => HASH_KEY_PATTERN.test(key);
const createHashRecord = <T>() => Object.create(null) as HashRecord<T>;

const withoutKey = <T>(source: HashRecord<T>, key: string): HashRecord<T> => {
  const { [key]: ignoredValue, ...rest } = source;
  return rest as HashRecord<T>;
};

const parseHashString = (hashString: string): HashParams =>
  hashString.split("&").reduce<HashParams>((acc, pair) => {
    const firstEqualIndex = pair.indexOf("=");
    if (firstEqualIndex === -1) {
      return acc;
    }
    const key = pair.slice(0, firstEqualIndex);
    const value = pair.slice(firstEqualIndex + 1);
    if (key && value && isSafeHashKey(key)) {
      return { ...acc, [key]: decodeURIComponent(value) };
    }
    return acc;
  }, createHashRecord<string>());

type UseElementNavigationOptions = {
  /**
   * Controls whether scrollToElement() can be called multiple times.
   *
   * false (default): scrollToElement() works only once (typically for initial deep link page load),
   * then stops working entirely. Prevents jarring scrolls during navigation.
   * Call resetScrollTracking() to re-enable scrolling.
   *
   * true: scrollToElement() works every time it's called, regardless of previous scrolls.
   *
   * Default: false
   */
  scrollOnHashChange?: boolean;
  /**
   * Default scrollIntoView options for all scroll operations.
   * Can be overridden per-call in scrollToElement.
   */
  scrollOptions?: ScrollIntoViewOptions;
  /**
   * Maximum time (in milliseconds) to wait for an element to appear before giving up.
   * Default: 1000 (1 second)
   */
  elementWaitTimeout?: number;
};

/**
 * Hook for managing URL anchor-based navigation and deep linking DOM elements.
 * Supports key=value pairs in the URL hash.
 *
 * Example: "action=123&batch=456&tab=users" becomes:
 * { action: "123", batch: "456", tab: "users" }
 *
 * Typical use:
 *   const { currentHash, updateHash, scrollToElement } = useElementNavigation();
 */
export function useElementNavigation(options?: UseElementNavigationOptions) {
  const location = useLocation();
  const history = useHistory();

  const hasInitialScrollOccurredRef = useRef(false);
  const rafIdRef = useRef<number>();
  const observerRefsRef = useRef<Map<string, MutationObserver>>(new Map());
  const timeoutRefsRef = useRef<Map<string, number>>(new Map());
  const isMountedRef = useRef(true);

  const scrollOnHashChange = useMemo(
    () => options?.scrollOnHashChange ?? false,
    [options?.scrollOnHashChange]
  );
  const defaultScrollOptions: ScrollIntoViewOptions = useMemo(
    () => options?.scrollOptions ?? { behavior: "smooth", block: "center" },
    [options?.scrollOptions]
  );
  const elementWaitTimeout = useMemo(
    () => options?.elementWaitTimeout ?? 1000,
    [options?.elementWaitTimeout]
  );

  const parseHash = useCallback((): HashParams | undefined => {
    const hash = location.hash.slice(1);

    if (!hash) {
      return undefined;
    }

    const params = parseHashString(hash);

    return Object.keys(params).length > 0 ? params : undefined;
  }, [location.hash]);

  const [initialHash] = useState(parseHash);
  const currentHash = useMemo(parseHash, [parseHash]);

  const updateHash = useCallback(
    (updates: HashUpdates, updateOptions?: { merge?: boolean }) => {
      const current =
        updateOptions?.merge && history.location.hash.slice(1)
          ? parseHashString(history.location.hash.slice(1))
          : createHashRecord<string>();

      const updated = Object.entries(updates).reduce((acc, [key, value]) => {
        if (!isSafeHashKey(key)) {
          return acc;
        }

        if (value === null) {
          return withoutKey(acc, key);
        }

        return { ...acc, [key]: value };
      }, current);

      const newHash =
        Object.keys(updated).length > 0
          ? `#${Object.entries(updated)
              .map(([k, v]) => [k, encodeURIComponent(v)].join("="))
              .join("&")}`
          : "";

      if (history.location.hash !== newHash) {
        history.replace({ ...history.location, hash: newHash });
      }
    },
    [history]
  );

  const cleanupElementObserver = useCallback((elementId: string) => {
    const observer = observerRefsRef.current.get(elementId);

    observer?.disconnect();
    observerRefsRef.current.delete(elementId);

    const timeoutId = timeoutRefsRef.current.get(elementId);

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefsRef.current.delete(elementId);
    }
  }, []);

  const performScroll = useCallback(
    (element: HTMLElement, scrollOptions?: ScrollIntoViewOptions) => {
      if (!isMountedRef.current) {
        return;
      }

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        try {
          element.scrollIntoView({ ...defaultScrollOptions, ...scrollOptions });

          if (!scrollOnHashChange) {
            hasInitialScrollOccurredRef.current = true;
          }
        } catch {}
      });
    },
    [scrollOnHashChange, defaultScrollOptions]
  );

  const scrollToElement: ScrollToElementFn = useCallback(
    (elementId: string, scrollOptions?: ScrollIntoViewOptions) => {
      if (
        !isMountedRef.current ||
        (!scrollOnHashChange && hasInitialScrollOccurredRef.current)
      ) {
        return;
      }

      cleanupElementObserver(elementId);

      const existingElement = document.getElementById(elementId);

      if (existingElement) {
        performScroll(existingElement, scrollOptions);
        return;
      }

      const observer = new MutationObserver(() => {
        if (!isMountedRef.current) {
          cleanupElementObserver(elementId);
          return;
        }

        const element = document.getElementById(elementId);

        if (element) {
          cleanupElementObserver(elementId);
          performScroll(element, scrollOptions);
        }
      });

      observerRefsRef.current.set(elementId, observer);
      observer.observe(document.body, { childList: true, subtree: true });

      const timeoutId = window.setTimeout(
        () => cleanupElementObserver(elementId),
        elementWaitTimeout
      );

      timeoutRefsRef.current.set(elementId, timeoutId);
    },
    [
      scrollOnHashChange,
      performScroll,
      cleanupElementObserver,
      elementWaitTimeout,
    ]
  );

  const resetScrollTracking = useCallback(() => {
    hasInitialScrollOccurredRef.current = false;
  }, []);

  useEffect(() => {
    const observersMap = observerRefsRef.current;
    const timeoutsMap = timeoutRefsRef.current;

    return () => {
      isMountedRef.current = false;

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      observersMap.forEach((observer) => observer.disconnect());
      observersMap.clear();

      timeoutsMap.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutsMap.clear();
    };
  }, []);

  return {
    initialHash,
    currentHash,
    updateHash,
    scrollToElement,
    resetScrollTracking,
  };
}
